// Offline sim data — ported from the prototype's data.js (mock-only parts).
// Used in MOCK mode and as the e2e test harness. Shapes mirror what the live
// source produces after mapping, so components are mode-agnostic.
import { vwap } from '../lib/format';

function mulberry32(a: number) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function hash(str: string) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

export type SymbolMeta = {
  ticker: string; name: string; base: number; chg: number; vol: number; rvol: number; prevClose?: number;
};

export const SYMBOLS: SymbolMeta[] = [
  { ticker: 'NUVX', name: 'Nuvectra Bio', base: 7.42, chg: 0.184, vol: 18.4, rvol: 6.2 },
  { ticker: 'GLXR', name: 'Galaxor Labs', base: 3.18, chg: 0.412, vol: 42.1, rvol: 11.8 },
  { ticker: 'TKAT', name: 'Takat Energy', base: 12.86, chg: -0.061, vol: 8.9, rvol: 2.1 },
  { ticker: 'ASTI', name: 'Ascent Solar', base: 1.94, chg: 0.231, vol: 33.6, rvol: 8.7 },
  { ticker: 'BNTC', name: 'Benitec Holdings', base: 5.71, chg: -0.118, vol: 14.2, rvol: 4.4 },
  { ticker: 'MULN', name: 'Mullaney Drive', base: 2.63, chg: 0.087, vol: 21.7, rvol: 3.3 },
  { ticker: 'CRKN', name: 'Crown Electrokin', base: 0.88, chg: 0.339, vol: 51.3, rvol: 14.1 },
  { ticker: 'VRAX', name: 'Virax Biolabs', base: 4.05, chg: 0.142, vol: 9.8, rvol: 5.0 },
  { ticker: 'HYMC', name: 'Hycroft Mining', base: 9.27, chg: -0.044, vol: 6.1, rvol: 1.8 },
  { ticker: 'INDO', name: 'Indo Resources', base: 6.13, chg: 0.268, vol: 27.9, rvol: 9.4 },
  { ticker: 'COSM', name: 'Cosmos Health', base: 1.42, chg: 0.512, vol: 63.8, rvol: 18.6 },
  { ticker: 'AREB', name: 'American Rebel', base: 3.77, chg: -0.092, vol: 11.5, rvol: 3.9 },
];
export const META: Record<string, SymbolMeta> = {};
SYMBOLS.forEach((s) => { META[s.ticker] = s; s.prevClose = +(s.base / (1 + s.chg)).toFixed(4); });

const TF_MIN: Record<string, number> = { '1': 1, '5': 5, '15': 15 };

type Candle = { time: number; open: number; high: number; low: number; close: number; value: number; up: boolean };

/* Simulated NBBO for MOCK mode. The live app reads `quotes:{SYM}`; offline there is
   no book at all, which left the header's bid × ask permanently empty and made the
   mock terminal look broken rather than offline. Spread scales with price so the
   basis-point readout lands in a believable band, and it wobbles by a tick so the
   staleness treatment and the tabular-numeral layout both get exercised. */
export function quoteTicker(symbol: string, cb: (q: any) => void): () => void {
  const m = META[symbol];
  let px = (m && m.base) || 5;
  const tick = px < 1 ? 0.0001 : 0.01;
  const iv = setInterval(() => {
    px = Math.max(tick, px + (Math.random() - 0.5) * tick * 4);
    const half = tick * (1 + Math.floor(Math.random() * 3)) / 2;
    const bid = +(px - half).toFixed(px < 1 ? 4 : 2);
    const ask = +(px + half).toFixed(px < 1 ? 4 : 2);
    cb({ symbol, bid, ask, bidSize: 1 + Math.floor(Math.random() * 40), askSize: 1 + Math.floor(Math.random() * 40), timestamp: new Date().toISOString() });
  }, 400);
  return () => clearInterval(iv);
}

export function genCandles(ticker: string, tf: string, count = 130): Candle[] {
  const m = META[ticker] || SYMBOLS[0];
  const rnd = mulberry32(hash(ticker + ':' + tf));
  const tfMin = TF_MIN[tf] || 1;
  const last = m.base;
  const vol = Math.max(0.004, 0.012 * Math.sqrt(tfMin)) * last;
  const closes = new Array(count);
  closes[count - 1] = last;
  const drift = (m.chg >= 0 ? 1 : -1) * vol * 0.18;
  for (let i = count - 2; i >= 0; i--) {
    const shock = (rnd() - 0.5) * 2 * vol;
    const burst = rnd() < 0.06 ? (rnd() - 0.45) * vol * 4 : 0;
    let prev = closes[i + 1] - drift - shock - burst;
    prev = Math.max(prev, 0.05);
    closes[i] = prev;
  }
  const now = Math.floor(Date.now() / 1000);
  const step = tfMin * 60;
  const startT = now - (count - 1) * step;
  const candles: Candle[] = [];
  for (let i = 0; i < count; i++) {
    const c = +closes[i].toFixed(4);
    const o = i === 0 ? +(c * (1 + (rnd() - 0.5) * 0.01)).toFixed(4) : candles[i - 1].close;
    const bodyHi = Math.max(o, c), bodyLo = Math.min(o, c);
    const wick = vol * (0.4 + rnd() * 1.1);
    const h = +(bodyHi + wick * rnd()).toFixed(4);
    const l = +(bodyLo - wick * rnd()).toFixed(4);
    const baseVol = (m.vol * 1e6) / count;
    const v = Math.round(baseVol * (0.4 + rnd() * 1.8) * (Math.abs(c - o) / vol + 0.5));
    candles.push({ time: startT + i * step, open: o, high: h, low: Math.max(l, 0.02), close: c, value: v, up: c >= o });
  }
  return candles;
}

export function keyLevels(candles: Candle[], ticker: string) {
  const m = META[ticker];
  let hi = -Infinity, lo = Infinity;
  for (const c of candles) { if (c.high > hi) hi = c.high; if (c.low < lo) lo = c.low; }
  const vw = vwap(candles);
  return {
    dayHigh: +hi.toFixed(2),
    dayLow: +lo.toFixed(2),
    pmHigh: +(hi * 0.985).toFixed(2),
    vwap: +vw[vw.length - 1].value.toFixed(2),
    prevClose: +(m.prevClose ?? m.base).toFixed(2),
  };
}

export function computeLevels(symbol: string) {
  const m = META[symbol];
  const rnd = mulberry32(hash(symbol + ':lvls'));
  const base = m.base;
  const prec = base < 1 ? 4 : 2;
  const tfsAll = ['1m', '3m', '5m', '15m', '1h', 'D'];
  const out: any[] = [];
  const n = 9 + Math.floor(rnd() * 4);
  for (let i = 0; i < n; i++) {
    const frac = (rnd() * 2 - 1) * 0.17;
    const px = +(base * (1 + frac)).toFixed(prec);
    if (Math.abs(px - base) / base < 0.011) continue;
    if (out.some((l) => Math.abs(l.px - px) / base < 0.018)) continue;
    const touches = 2 + Math.floor(rnd() * 7);
    const tfCount = 1 + Math.floor(rnd() * 5);
    const timeframes: string[] = [];
    const pool = tfsAll.slice();
    for (let k = 0; k < tfCount; k++) timeframes.push(pool.splice(Math.floor(rnd() * pool.length), 1)[0]);
    timeframes.sort((a, b) => tfsAll.indexOf(a) - tfsAll.indexOf(b));
    const trades = Math.round((3500 + rnd() * 52000) * (touches / 3) * (0.6 + tfCount / 6));
    const strength = Math.min(1, (touches / 8) * 0.45 + (tfCount / 6) * 0.4 + rnd() * 0.15);
    const lastTested = ['18m', '47m', '1h', '2h', '4h', 'Mon', 'prev wk'][Math.floor(rnd() * 7)];
    out.push({ id: 'L' + i, px, touches, timeframes, trades, strength: +strength.toFixed(2), lastTested });
  }
  out.sort((a, b) => b.px - a.px);
  return out;
}

export function defaultPositions() {
  return {
    NUVX: { side: 'long', qty: 1500, avg: 7.05, targets: [{ id: 't1', t: 'T1', px: 7.80, qty: 750 }, { id: 't2', t: 'T2', px: 8.25, qty: 750 }], stop: { id: 'st', px: 6.78, qty: 1500 }, entries: [] },
    TKAT: { side: 'short', qty: 800, avg: 13.40, targets: [{ id: 't1', t: 'T1', px: 12.40, qty: 800 }], stop: { id: 'st', px: 13.78, qty: 800 }, entries: [] },
    GLXR: { side: 'long', qty: 4000, avg: 3.34, targets: [{ id: 't1', t: 'T1', px: 3.62, qty: 2000 }, { id: 't2', t: 'T2', px: 3.95, qty: 2000 }], stop: { id: 'st', px: 3.05, qty: 4000 }, entries: [] },
  } as Record<string, any>;
}

// Sim account cash (mock-only) — live mode uses /api/account.
export const MOCK_CASH = 84230.55;

// ---- screener / automated-signals mock (offline only) ----
const SIGNAL_POOL = SYMBOLS.map((s) => s.ticker);
// `short` builds the sell-led mirror: price move negative, sell-dominant flow.
// Same field names — the screener's DOWN alerts reuse the surge shape, so the
// mock has to as well or the card renders differently offline than live.
function buildSignal(ticker: string, r: () => number, ts: number, short = false) {
  const m = META[ticker] || SYMBOLS[0];
  const prec = m.base < 1 ? 4 : 2;
  const bucket = r() < 0.6 ? 'PRE_NEWS' : 'ON_NEWS';
  const score = +(0.55 + r() * 0.44).toFixed(3);
  const price = +(m.base * (1 + (r() - 0.5) * 0.05)).toFixed(prec);
  const priceMovePct = short ? +(-(0.2 + r() * 1.0)).toFixed(2) : +(((r() - 0.28) * 1.1)).toFixed(2);
  const surgeVol = Math.round(60 + r() * 2600) * 10;
  const rvol = +(6 + r() * (r() < 0.22 ? 640 : 60)).toFixed(0);
  const buyFrac = short ? +(0.04 + r() * 0.31).toFixed(2) : +(0.5 + r() * 0.46).toFixed(2);
  const buyVol = Math.round(surgeVol * buyFrac);
  return {
    id: 'sig_' + ticker + '_' + ts + '_' + Math.floor(r() * 1e4),
    symbol: ticker, bucket, score, price, priceMovePct,
    rvol, surgeVol, buyVol, sellVol: surgeVol - buyVol, buyFrac,
    sweep: Math.floor(r() * 5), ts,
  };
}
export function genSignal() {
  return buildSignal(SIGNAL_POOL[Math.floor(Math.random() * SIGNAL_POOL.length)], Math.random, Date.now());
}
export function seedSignals(n: number) {
  const now = Date.now();
  const rnd = mulberry32(hash('seed'));
  const out: any[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(buildSignal(SIGNAL_POOL[Math.floor(rnd() * SIGNAL_POOL.length)], rnd, now - i * (45000 + Math.floor(rnd() * 120000))));
  return out;
}
export function genShortSignal() {
  return buildSignal(SIGNAL_POOL[Math.floor(Math.random() * SIGNAL_POOL.length)], Math.random, Date.now(), true);
}
export function seedShortSignals(n: number) {
  const now = Date.now();
  // Distinct seed from seedSignals so the two offline feeds don't mirror each
  // other symbol-for-symbol.
  const rnd = mulberry32(hash('seed-short'));
  const out: any[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(buildSignal(SIGNAL_POOL[Math.floor(rnd() * SIGNAL_POOL.length)], rnd, now - i * (45000 + Math.floor(rnd() * 120000)), true));
  return out;
}

// ---- structure map (mock screener:levels schema) for offline mode ----
const SUPPORT_CATALOG: [string, number, number, number][] = [
  ['VWAP', -0.25, 1, 4], ['5m_EMA21', -0.55, 1, 4], ['5m_EMA50', -0.9, 1, 3],
  ['15m_EMA9', -0.65, 1, 3], ['15m_EMA21', -1.0, 1, 4], ['15m_EMA50', -1.45, 1, 3],
  ['5m_SwingLow', -1.6, 1, 4], ['15m_SwingLow', -1.72, 1, 4], ['1H_SwingLow', -2.6, 1, 5],
  ['4H_SwingLow', -4.0, 1, 5], ['5m_EMA9', -0.45, 2, 2], ['PriorDayLow', -4.1, 2, 4],
  ['PriorWeekLow', -5.3, 2, 3], ['1W_EMA21', -3.2, 2, 2], ['15m_EMA200', -6.6, 3, 0],
];
const RESIST_CATALOG: [string, number, number, number][] = [
  ['PriorDayHigh', -0.4, 1, 5], ['5m_SwingHigh', 0.8, 1, 3], ['15m_SwingHigh', 1.4, 1, 4],
  ['1H_SwingHigh', 2.5, 1, 5], ['4H_SwingHigh', 4.2, 1, 5], ['1W_SwingHigh', 5.7, 1, 6],
  ['PriorWeekHigh', 3.0, 1, 6], ['SessionHigh', 6.6, 1, 4],
];
export function computeStructure(symbol: string, lastPrice?: number) {
  const m = META[symbol] || SYMBOLS[0];
  const price = lastPrice || m.base;
  const rnd = mulberry32(hash(symbol + ':struct'));
  const prec = price < 1 ? 4 : 2;
  const atr = +(price * 0.043).toFixed(4);
  const now = Date.now();
  const ago = (mn: number) => now - Math.round(mn * 60000);
  const supports = SUPPORT_CATALOG.map(([type, off, tier, bw], i) => {
    const lvlPrice = +(price + off * atr).toFixed(prec);
    const weight = Math.max(0, Math.min(6, bw + (rnd() < 0.3 ? 1 : 0) - (rnd() < 0.2 ? 1 : 0)));
    const touchCount = tier === 3 ? 1 + Math.floor(rnd() * 4) : 8 + Math.floor(rnd() * 50);
    const defenseCount = tier === 3 ? 0 : Math.floor(rnd() * (weight >= 4 ? 5 : 3));
    let status = 'WATCHING';
    if (type === 'VWAP' && rnd() < 0.6) status = 'LOST';
    else if (defenseCount >= 3 && weight >= 4) status = 'DEFENDED';
    else if (tier !== 3 && rnd() < 0.12) status = 'LOST';
    const totalBuyVolume = Math.round((tier === 3 ? 2000 : 30000) * (0.4 + rnd() * 3) * (weight + 1));
    return { type, tier, price: lvlPrice, weight, status, role: 'support', touchCount, defenseCount,
      totalBuyVolume, buyVolumeAtLevel: Math.round(totalBuyVolume * (0.05 + rnd() * 0.22)),
      lastTouchAt: ago(2 + rnd() * 180), lastDefenseAt: defenseCount ? ago(5 + rnd() * 120) : null, id: 'S' + i };
  });
  const resistances = RESIST_CATALOG.map(([type, off, tier, bw], i) => {
    const lvlPrice = +(price + off * atr).toFixed(prec);
    const weight = Math.max(1, Math.min(6, bw + (rnd() < 0.3 ? 1 : 0)));
    const below = lvlPrice < price;
    const totalSellVolume = Math.round(20000 * (0.4 + rnd() * 3) * weight);
    return { type, tier, price: lvlPrice, weight, status: below ? 'BROKEN' : 'WATCHING', role: 'resistance',
      barsAbove: below ? Math.floor(rnd() * 12) : 0, rejections: below ? Math.floor(rnd() * 2) : Math.floor(rnd() * 4),
      totalSellVolume, sellVolumeAtLevel: Math.round(totalSellVolume * (0.05 + rnd() * 0.2)),
      lastTouchAt: ago(2 + rnd() * 160), id: 'R' + i };
  });
  const fl1 = +(price - 0.35 * atr).toFixed(prec);
  const flipped = [{ price: fl1, status: rnd() < 0.5 ? 'AT' : 'WATCHING', role: 'flipped',
    wasResistanceAt: ago(420 + rnd() * 300), brokeAt: ago(360 + rnd() * 120),
    timesTestedAsSupport: 1 + Math.floor(rnd() * 4), rejectionsAsResistance: 1 + Math.floor(rnd() * 3),
    buyVolumeAtLevel: Math.round(rnd() * 18000), id: 'F0' }];
  return { symbol, updatedAt: now, sessionATR: atr, supports, resistances, flipped };
}
